/**
 * ProviderOSGate — runtime entitlement check.
 *
 * Wraps any Provider OS page; if the caller's org does NOT have the
 * `provider_os` flag, renders a friendly "Not enabled for your org"
 * screen instead of letting the page hit 402s on every request.
 *
 * This is a UX gate; the backend remains the authority (every
 * /api/provider/* endpoint is independently gated server-side).
 */
import React from 'react';
import { Lock, ExternalLink } from 'lucide-react';
import { useOrgEntitlements } from '../../hooks/useOrgEntitlements';
import { Link } from 'react-router-dom';

export const ProviderOSGate = ({ children }) => {
  const { loading, hasFlag } = useOrgEntitlements();

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500">
        <div className="animate-pulse" data-testid="provider-os-loading">
          Loading provider workspace…
        </div>
      </div>
    );
  }

  if (!hasFlag('provider_os')) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6"
           data-testid="provider-os-gate-locked">
        <div className="max-w-xl text-center space-y-5">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-100">
            <Lock className="w-7 h-7 text-slate-500" />
          </div>
          <h1 className="text-3xl font-semibold text-slate-900">
            Provider OS isn&apos;t enabled for your organisation yet
          </h1>
          <p className="text-base text-slate-600 leading-relaxed">
            Provider OS is the supply-side workspace that opens on each
            accepted placement — incoming work, paperwork (F2F, 485 POC,
            NOA, consent), the shared first-visit window, and the payout
            ledger.
            <br /><br />
            Contact CaraLoom Ops to enable it for your org.
          </p>
          <div className="flex items-center justify-center gap-3 pt-2">
            <a
              href="mailto:ops@caraloom.com?subject=Enable%20Provider%20OS"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full
                          bg-slate-900 text-white text-sm font-medium hover:bg-slate-800
                          transition-colors"
              data-testid="provider-os-contact-ops"
            >
              <ExternalLink className="w-4 h-4" />
              Email Ops
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full
                          border border-slate-200 text-slate-700 text-sm font-medium
                          hover:bg-slate-50 transition-colors"
              data-testid="provider-os-back-home"
            >
              Back home
            </Link>
          </div>
        </div>
      </div>
    );
  }
  return children;
};

export default ProviderOSGate;
